import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  Paper,
  TableCell,
  TableBody,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useAppDispatch } from '../../../app/hooks';
import { loadHistoryForBatch, UrsRequestMatchingHistory } from '../ursOfferMachingHistoryApi';
import { NoteLabel } from '../NoteLabel';
import { RequirementNote } from '../ursOfferSlice';

const MatchingReportPage: React.FC = () => {
  const { batchId = '' } = useParams();
  const dispatch = useAppDispatch();

  const [data, setData] = useState<UrsRequestMatchingHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedDetail, setSelectedDetail] = useState<UrsRequestMatchingHistory | null>(null);

  useEffect(() => {
    if (!batchId) return;
    setLoading(true);
    dispatch(loadHistoryForBatch({ batchId }))
      .unwrap()
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [batchId]);

  const handleOpenDetail = (row: UrsRequestMatchingHistory) => {
    setSelectedDetail(row);
  };

  const handleCloseDetail = () => {
    setSelectedDetail(null);
  };


  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Matching report – batch #{batchId}
      </Typography>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Showing {data.length} matching result{data.length !== 1 ? 's' : ''}
      </Typography>
      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Requirement Text</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Note</TableCell>
                <TableCell>Matched Requirement</TableCell>
                <TableCell>URS number (Matched)</TableCell>


                <TableCell>SBERT</TableCell>
                <TableCell>Levenshtein</TableCell>

              </TableRow>

            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id} hover onClick={() => handleOpenDetail(row)} sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <div dangerouslySetInnerHTML={{ __html: row.sourceText }} />
                  </TableCell>
                  <TableCell
                    sx={{
                      borderLeft: "2px solid #ddd",
                      backgroundColor: "#f5f5f5",
                    }}
                  >{row.comment || '-'}</TableCell>
                  <TableCell sx={{
                    textAlign: "center", borderLeft: "2px solid #ddd",
                    backgroundColor: "#f5f5f5",
                  }}>
                    <NoteLabel note={row.note as RequirementNote ?? ''} />
                  </TableCell>
                  <TableCell>
                    <div dangerouslySetInnerHTML={{ __html: row.matchedText }} />
                  </TableCell>
                  <TableCell>{row.matchedUrsNumber ? `${row.matchedUrsNumber}` : '-'}</TableCell>

                  <TableCell>{(row.sbertSimilarity).toFixed(1)}%</TableCell>
                  <TableCell>{(row.levenshteinSimilarity).toFixed(1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!selectedDetail} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        <DialogTitle>Szczegóły dopasowania</DialogTitle>
        <DialogContent>
          {selectedDetail && (
            <Box>
              <Typography variant="subtitle1">Numer: {selectedDetail.sourceRequirementId}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Treść:</Typography>
              <Typography>{selectedDetail.sourceText}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Dopasowanie:</Typography>
              <Typography>{selectedDetail.matchedText}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Oferta: {selectedDetail.matchedUrsNumber}</Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Levenshtein: {(selectedDetail.levenshteinSimilarity * 100).toFixed(1)}%</Typography>
              <Typography variant="subtitle2">SBERT: {(selectedDetail.sbertSimilarity * 100).toFixed(1)}%</Typography>
              {selectedDetail.comment && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Komentarz:</Typography>
                  <Typography>{selectedDetail.comment}</Typography>
                </>
              )}
              {selectedDetail.note !== undefined && (
                <Typography sx={{ mt: 2 }}>Ocena: {selectedDetail.note}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Zamknij</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MatchingReportPage;
